import { PageLayout } from "@/components";
import { useTranslation } from "@/hooks";
import { PaymentActions } from "@/pages/payment/shared";
import { Route } from "@/routes/_market/payment/index";
import { INTENT_PERMISSIONS, userIntentAtom } from "@/store/login";
import { Icon, TabItem, Tabs, TabsList } from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { PaymentReceived, PaymentSent } from "./components";

/** Payments you sent vs payments you received; the URL carries it so it can be linked to. */
type PaymentTab = "sent" | "received";

function PaymentPage() {
	const { t } = useTranslation();
	const userIntent = useAtomValue(userIntentAtom);
	const navigate = useNavigate({ from: "/payment" });
	const { tab } = Route.useSearch();

	const intentPermissions = userIntent
		? INTENT_PERMISSIONS[userIntent].payment
		: null;
	const showOutgoing = intentPermissions?.showSentTab ?? true;
	const showIncoming = intentPermissions?.showReceivedTab ?? true;
	const defaultTab: PaymentTab =
		intentPermissions?.defaultTab === "sent" ? "sent" : "received";

	const isAllowed = (value: PaymentTab) =>
		value === "sent" ? showOutgoing : showIncoming;

	const activeTab: PaymentTab = tab && isAllowed(tab) ? tab : defaultTab;

	const setTab = (next: PaymentTab) =>
		navigate({ search: { tab: next }, replace: true });

	// Keep the URL honest: a tab this intent can't see shouldn't stay in it.
	useEffect(() => {
		if (tab && !isAllowed(tab)) setTab(defaultTab);
	});

	return (
		<PageLayout>
			<div className="mb-1 flex flex-col-reverse items-stretch justify-between md:flex-row md:items-center gap-4 md:gap-0">
				<Tabs
					value={activeTab}
					onValueChange={(value) => setTab(value as PaymentTab)}
				>
					<TabsList>
						{showIncoming && (
							<TabItem value="received" Icon={<Icon name="hand-coin-line" />}>
								{t("payment.received")}
							</TabItem>
						)}
						{showOutgoing && (
							<TabItem
								value="sent"
								Icon={<Icon name="money-dollar-circle-line" />}
							>
								{t("payment.sent")}
							</TabItem>
						)}
					</TabsList>
				</Tabs>

				<PaymentActions />
			</div>
			<div className="mt-2">
				{activeTab === "received" ? <PaymentReceived /> : <PaymentSent />}
			</div>
		</PageLayout>
	);
}

export default PaymentPage;
